import { subscribe } from "@/api/api";
import { PublicKnowledge } from "@/components/HeroPages/PublicKnowledge";
import { Results } from "@/components/HeroPages/Results";
import { Shield } from "@/components/HeroPages/Shield";
import { TakeControl } from "@/components/HeroPages/TakeControl";
import { Teaser } from "@/components/HeroPages/Teaser";
import { Verifications } from "@/components/HeroPages/Verifications";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  waitlistFormSchema,
  type WaitlistFormInput,
} from "@/schemas/subscribe";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [showDialog, setShowDialog] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<WaitlistFormInput>({ resolver: zodResolver(waitlistFormSchema) });

  const onSubmit: SubmitHandler<WaitlistFormInput> = async (data) => {
    try {
      await subscribe(data.email, "personal");
      setShowDialog(false);
      reset();
      toast.success("Tack! Du är på listan 🎉", {
        duration: 5_000,
        position: "top-center",
      });
    } catch {
      toast.error(
        "Något gick fel. Försök igen om en stund eller kontakta info@echorai.com.",
        {
          duration: 6_000,
          position: "top-center",
        }
      );
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="snap-start scroll-mt-20 min-h-[calc(100dvh-5rem)] flex flex-col justify-center py-16">
        <TakeControl setShowDialog={setShowDialog} />
      </Reveal>
      <Reveal className="snap-start scroll-mt-20 min-h-[calc(100dvh-5rem)] flex flex-col justify-center py-16">
        <PublicKnowledge />
      </Reveal>
      <Reveal className="snap-start scroll-mt-20 min-h-[calc(100dvh-5rem)] flex flex-col justify-center py-16">
        <Results />
      </Reveal>
      <Reveal className="snap-start scroll-mt-20 min-h-[calc(100dvh-5rem)] flex flex-col justify-center py-16">
        <Shield setShowDialog={setShowDialog} />
      </Reveal>
      <Reveal className="snap-start scroll-mt-20 min-h-[calc(100dvh-5rem)] flex flex-col justify-center py-16">
        <Verifications />
      </Reveal>
      <Reveal className="snap-start scroll-mt-20 min-h-[calc(100dvh-5rem)] flex flex-col justify-center py-16">
        <Teaser setShowDialog={setShowDialog} />
      </Reveal>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Prenumerera</DialogTitle>
            <DialogDescription>
              Prenumerera för att vara först med att prova Echorai
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4">
              <div className="grid gap-3">
                <Label htmlFor="in-email">E-post</Label>
                <Input
                  id="in-email"
                  type="email"
                  autoComplete="email"
                  placeholder="example@mail.com"
                  disabled={isSubmitting}
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-xs text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>
            <DialogFooter className="mt-2">
              <DialogClose asChild>
                <Button variant="outline" disabled={isSubmitting}>
                  Avbryt
                </Button>
              </DialogClose>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Skickar…" : "Skicka"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
